import { ExternalLinkIcon } from 'lucide-react';
import type { Project } from '@/api/projects/types';
import AndroidIcon from '@/assets/platform-icons/android-icon-small.svg?react';
import IosIcon from '@/assets/platform-icons/apple-icon.svg?react';
import FlutterIcon from '@/assets/platform-icons/flutter-icon.svg?react';
import ReactNativeIcon from '@/assets/platform-icons/react-icon.svg?react';
import { CopyToClipboardButton } from '@/components/copy-to-clipboard-button';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type Props = {
  isOpen: boolean;
  project: Project | null;
  setIsOpen: (value: boolean) => void;
};

const LINKS = [
  {
    icon: ReactNativeIcon,
    platform: 'React Native',
    link: 'https://docs.featurebuddy.com/docs/react-native/getting-started',
    disabled: false,
  },
  {
    icon: FlutterIcon,
    platform: 'Flutter',
    link: 'https://docs.featurebuddy.com/docs/flutter/getting-started',
    disabled: false,
  },
  {
    icon: IosIcon,
    platform: 'iOS',
    link: 'https://docs.featurebuddy.com/docs/ios/getting-started',
    disabled: true,
    className: 'text-black dark:text-white',
  },
  {
    icon: AndroidIcon,
    platform: 'Android',
    link: 'https://docs.featurebuddy.com/docs/android/getting-started',
    disabled: true,
  },
];

type ButtonLinkProps = {
  Icon: React.ElementType;
  platform: string;
  disabled: boolean;
};

const ButtonLink = ({ Icon, platform, disabled }: ButtonLinkProps) => {
  return (
    <Button
      variant="link"
      asChild
      className="flex h-48 w-48 border-1 hover:bg-card/50 hover:border-muted-foreground/75"
      key={platform}
      disabled={disabled}
    >
      <div className="flex flex-col items-center justify-center gap-2">
        <Icon className="size-16" />
        <div className="flex items-center gap-1.5">
          <span className="text-sm">{platform}</span>
          {!disabled && <ExternalLinkIcon className="text-muted-foreground" />}
        </div>
        {disabled && <span className="text-sm text-muted-foreground">Coming soon</span>}
      </div>
    </Button>
  );
};

const ProjectSetupDialog = ({ isOpen, project, setIsOpen }: Props) => {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="flex flex-col gap-6 p-6">
        <DialogHeader>
          <DialogTitle>Setup your project</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <p>Copy this key and follow instructions below to setup your project.</p>
          <p>Later you can find this key in your project settings.</p>
        </DialogDescription>
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="link" className="text-sm text-muted-foreground">
              API Key
            </Label>
            {/** biome-ignore lint/correctness/useUniqueElementIds: <explanation> */}
            <Input id="link" defaultValue={project?.apiKey} readOnly />
          </div>
          <CopyToClipboardButton
            value={project?.apiKey || ''}
            toastMessage="API key copied to clipboard"
          />
        </div>

        <span className="text-sm font-medium text-muted-foreground text-center">
          Integration documentation:
        </span>

        <div className="grid grid-cols-2 place-content-around place-items-center gap-4">
          {LINKS.map(({ icon: Icon, platform, link, disabled }) => {
            if (disabled) {
              return (
                <ButtonLink key={platform} Icon={Icon} platform={platform} disabled={disabled} />
              );
            }

            return (
              <a href={link} target="_blank" rel="noreferrer" key={platform}>
                <ButtonLink key={platform} Icon={Icon} platform={platform} disabled={disabled} />
              </a>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSetupDialog;
